/**
 * Ask spend, on the Usage page: balance, 30-day spend and a daily chart.
 * Buying lives on Billing; this links there.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTeam } from 'shared/context/teamContext';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { getCredits } from 'features/settings/api/creditsApi';
import {
  usd, Section, Stat, DailyBars, linkBtn, MUTED,
} from 'features/settings/components/panels/askCreditsShared';

export default function AskSpendCard() {
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id || selectedTeam?._id;
  const billingHref = selectedTeam?.name
    ? `/${encodeURIComponent(selectedTeam.name)}/settings/billing`
    : '/settings/billing';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      setData(await getCredits(teamId));
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not load Lucid spend');
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const bars = useMemo(() => (data?.burn || []).map((b) => ({
    key: b.day,
    label: new Date(b.day).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: Number(b.charged_usd) || 0,
  })), [data]);
  const spent30d = useMemo(() => bars.reduce((s, b) => s + b.value, 0), [bars]);

  if (!teamId) return null;

  const balance = data?.balance || {};
  const disabled = balance.credits_enabled === false;

  return (
    <Section
      title="Lucid"
      action={!disabled && !loading ? <Link to={billingHref} className={linkBtn}>Add credit</Link> : null}
    >
      {loading && !data ? (
        <BrandLoader size="md" label="Loading Lucid" />
      ) : error && !data ? (
        <div className="flex items-center gap-3 text-[12px]" style={{ color: MUTED }}>
          {error}
          <button type="button" className={linkBtn} onClick={load}>Retry</button>
        </div>
      ) : disabled ? (
        <div className="text-[12px]" style={{ color: MUTED }}>Lucid runs on your own model keys.</div>
      ) : (
        <>
          <div className="mb-5 flex items-end gap-10">
            <Stat big label="Remaining" value={usd(balance.total_usd)} />
            <Stat label="Spent, 30 days" value={usd(spent30d)} />
          </div>
          {spent30d > 0 ? <DailyBars data={bars} format={usd} /> : null}
        </>
      )}
    </Section>
  );
}
